/**
 * 一番賞前台四個呼叫端（draw-api.mjs / gacha-draw-api.mjs 的 sendShared /
 * draw-hold.mjs / lottery_product_single_page.js 的 queueRequest()）共用的
 * REST 傳輸層。收斂三件事：base URL 組法、X-WP-Nonce header、HTTP 401/403
 * 統一轉成與業務錯誤同形狀的回應（code = session_expired）。
 */

const SESSION_EXPIRED_MESSAGE = '頁面版本已過期，請重新整理後再操作。';
const GENERIC_CONNECTION_ERROR_MESSAGE = '連線發生問題，請稍後再試。';

/**
 * HTTP status 轉回應形狀的純函式。401/403（登入過期／nonce 過期）與其他非 2xx
 * （框架層錯誤、伺服器錯誤）統一轉成 `{success:false, data:{message, code?}}`，
 * 讓下游沿用既有的 `res.success` 分岔邏輯，不必額外認得 REST 的錯誤形狀。
 *
 * @param {number} status
 * @param {*} body 已解析的 JSON body（200 時原樣回傳）
 * @returns {{success: boolean, data: object}}
 */
export function translateStatus(status, body) {
    if (status === 401 || status === 403) {
        return { success: false, data: { message: SESSION_EXPIRED_MESSAGE, code: 'session_expired' } };
    }
    if (status >= 200 && status < 300) {
        return body;
    }
    return { success: false, data: { message: GENERIC_CONNECTION_ERROR_MESSAGE } };
}

/**
 * 遞迴展開單一 key/value 進 URLSearchParams，貼齊 PHP 對巢狀參數的解析慣例：
 * 純量陣列 → `key[]=v1&key[]=v2`（PHP 自動編號）；物件與物件陣列 → `key[sub]=v`
 * / `key[N][sub]=v`（PHP 依此重建關聯陣列 / 索引陣列）。純量陣列維持 `key[]`
 * 而非索引形式，只是為了讓既有呼叫端的既有斷言（如 selection 展開）維持原樣，
 * 兩種寫法 PHP 端解析結果相同。
 * @param {URLSearchParams} usp
 * @param {string} key
 * @param {*} value
 */
function appendParam(usp, key, value) {
    if (value === undefined || value === null) {
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            if (item === undefined || item === null) {
                return;
            }
            if (typeof item === 'object') {
                appendParam(usp, `${key}[${index}]`, item);
            } else {
                usp.append(`${key}[]`, item);
            }
        });
        return;
    }
    if (typeof value === 'object') {
        Object.keys(value).forEach((subKey) => appendParam(usp, `${key}[${subKey}]`, value[subKey]));
        return;
    }
    usp.append(key, value);
}

/**
 * 把參數物件轉成 `URLSearchParams`。`URLSearchParams` 建構子遇到陣列或物件值
 * 只會呼叫 `String(value)` 壓成逗號字串或 `[object Object]`，後端收到的不會是
 * 陣列或關聯陣列，故一律走 appendParam() 遞迴展開。
 * @param {object} params
 * @returns {URLSearchParams}
 */
export function toURLSearchParams(params) {
    const usp = new URLSearchParams();
    Object.keys(params || {}).forEach((key) => appendParam(usp, key, params[key]));
    return usp;
}

/**
 * @param {string} method
 * @param {string} url
 * @param {string} nonce
 * @param {object} params
 * @returns {Promise<{success: boolean, data: object}>}
 */
async function defaultRequest(method, url, nonce, params) {
    // 空字串也不能送——REST cookie auth 判的是 header 有沒有出現（isset()），
    // 不是值是否為真，帶空值一樣會被當成「nonce 錯」判 403，公開端點反而連
    // 未過期的登入使用者都擋下來。
    const headers = nonce ? { 'X-WP-Nonce': nonce } : {};
    let target = url;
    let body;

    if (method === 'GET') {
        const qs = toURLSearchParams(params).toString();
        target = qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
    } else {
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
        body = toURLSearchParams(params);
    }

    const res = await fetch(target, { method, credentials: 'same-origin', headers, body });
    const json = await res.json().catch(() => null);

    return translateStatus(res.status, json);
}

/**
 * multipart 版本：body 原樣是傳入的 FormData，不跑 toURLSearchParams、不設
 * Content-Type（讓瀏覽器自帶 boundary），其餘（X-WP-Nonce、credentials、
 * translateStatus）與 defaultRequest() 一致。
 *
 * @param {string} url
 * @param {string} nonce
 * @param {FormData} formData
 * @returns {Promise<{success: boolean, data: object}>}
 */
async function defaultFormRequest(url, nonce, formData) {
    const headers = nonce ? { 'X-WP-Nonce': nonce } : {};

    const res = await fetch(url, { method: 'POST', credentials: 'same-origin', headers, body: formData });
    const json = await res.json().catch(() => null);

    return translateStatus(res.status, json);
}

/**
 * @param {{restBase: string, nonce: string, request?: function, formRequest?: function}} opts
 * `request(method, route, params)` 預設用 fetch 送出；測試時注入假的。
 * `formRequest(route, formData)` 供 postForm() 使用，預設用 fetch 送出；測試時注入假的。
 */
export function createRestClient({ restBase, nonce, request, formRequest }) {
    const send = request || ((method, route, params) => defaultRequest(method, `${restBase}${route}`, nonce, params));
    const sendForm = formRequest || ((route, formData) => defaultFormRequest(`${restBase}${route}`, nonce, formData));

    return {
        get(route, params) {
            return send('GET', route, params);
        },
        post(route, params) {
            return send('POST', route, params);
        },
        postForm(route, formData) {
            return sendForm(route, formData);
        },
    };
}
